import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faComment, faSpinner, faEnvelope, faTimes, faTrash, faEyeSlash, faGift } from '@fortawesome/free-solid-svg-icons';
import { Card, Form, Button, Table, InputGroup, Alert, Badge, Modal } from '@themesberg/react-bootstrap';
import { collection, query, getDocs, orderBy, limit, getDoc, doc, updateDoc, deleteDoc, onSnapshot, where, writeBatch, runTransaction, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { resolveChatRoomId } from "../../utils/chatUtils";
import { useHistory } from 'react-router-dom';

export default () => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [unrepliedMessages, setUnrepliedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIgnoreConfirm, setShowIgnoreConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showIgnoreAllConfirm, setShowIgnoreAllConfirm] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);
  // Gift modal state
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftTargetMsg, setGiftTargetMsg] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [giftsLoading, setGiftsLoading] = useState(false);
  const [sendingGift, setSendingGift] = useState(null); // giftId being sent

  useEffect(() => {
    // Real-time listener ile unansweredMessages koleksiyonunu dinle
    const unansweredQuery = query(
      collection(db, 'unansweredMessages'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(unansweredQuery, (snapshot) => {
      console.log(`UnrepliedMessages updated: ${snapshot.docs.length} messages`);

      // Aynı chatRoom için sadece en son mesajı tut
      const latestMessagesByRoom = new Map();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const roomId = data.chatRoomId;
        const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : new Date();

        // Eğer bu chatRoom için daha yeni bir mesaj yoksa ekle
        const existing = latestMessagesByRoom.get(roomId);
        if (!existing || timestamp > existing.date) {
          latestMessagesByRoom.set(roomId, {
            id: doc.id,
            roomId: roomId,
            sender: {
              id: data.senderId,
              name: data.senderName || 'Bilinmeyen',
              avatar: data.senderAvatar || null,
              city: data.senderCity || null,
              age: data.senderAge || null
            },
            receiver: {
              id: data.receiverId,
              name: data.receiverName || 'Doğrulanmış Üye',
              avatar: data.receiverAvatar || null,
              city: data.receiverCity || null,
              age: data.receiverAge || null
            },
            message: data.message || 'Mesaj',
            timestamp: data.timestamp,
            type: data.messageType || 'text',
            date: timestamp,
            isReplied: data.isReplied || false,
            mediaUrl: data.mediaUrl || null,
            durationMillis: data.durationMillis || null
          });
        }
      });

      // Map'i listeye çevir
      const unrepliedList = Array.from(latestMessagesByRoom.values());
      setUnrepliedMessages(unrepliedList);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to unreplied messages:', err);
      setError('Mesajlar yüklenirken bir hata oluştu: ' + err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Gift pool yükle
  const loadGifts = async () => {
    if (gifts.length > 0) return; // already loaded
    setGiftsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'gifts')));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(g => g.isActive !== false)
        .sort((a, b) => a.name.localeCompare(b.name));
      setGifts(list);
    } catch (e) {
      console.error('Error loading gifts:', e);
    } finally {
      setGiftsLoading(false);
    }
  };

  // Open gift picker for a row's receiver (fake account)
  const openGiftModal = (msg, e) => {
    e.stopPropagation();
    setGiftTargetMsg(msg);
    setShowGiftModal(true);
    loadGifts();
  };

  // Send gift: sender = fake account (receiver of original msg), target = real user (sender of orig msg)
  const handleSendGift = async (gift) => {
    if (!giftTargetMsg) return;
    const fakeUserId = giftTargetMsg.receiver.id;  // fake account uid
    const realUserId = giftTargetMsg.sender.id;    // real user uid
    const fakeUserName = giftTargetMsg.receiver.name;
    const chatRoomId = await resolveChatRoomId(fakeUserId, realUserId);

    setSendingGift(gift.id);
    try {
      // 1. Transaction: sender (fake)'s credits not needed — admin sends for free
      //    Just create notification + chat message
      const notifRef = doc(collection(db, 'notifications'));
      await addDoc(collection(db, 'notifications'), {
        userId: realUserId,
        senderId: fakeUserId,
        type: 'GIFT',
        title: 'Yeni Hediye!',
        body: `${fakeUserName} sana bir ${gift.name} gönderdi.`,
        imageUrl: gift.imageUrl || null,
        timestamp: serverTimestamp(),
        isRead: false
      });

      // 2. Chat message
      const roomRef = doc(db, 'chatRooms', chatRoomId);
      const messageRef = doc(collection(db, 'chatRooms', chatRoomId, 'messages'));
      const existingRoom = await getDoc(roomRef);
      const existingUnread = existingRoom.exists() ? (existingRoom.data().unreadCounts || {}) : {};
      const newUnread = { ...existingUnread, [realUserId]: ((existingUnread[realUserId] || 0) + 1) };

      await updateDoc(roomRef, {
        participantUids: [fakeUserId, realUserId],
        lastMessage: `${gift.name} 🎁`,
        lastMessageAt: serverTimestamp(),
        lastMessageType: 'gift',
        lastMessageSenderId: fakeUserId,
        unreadCounts: newUnread
      }).catch(async () => {
        // Room might not exist; use set
        const { setDoc } = await import('firebase/firestore');
        await setDoc(roomRef, {
          participantUids: [fakeUserId, realUserId],
          lastMessage: `${gift.name} 🎁`,
          lastMessageAt: serverTimestamp(),
          lastMessageType: 'gift',
          lastMessageSenderId: fakeUserId,
          unreadCounts: { [realUserId]: 1 }
        });
      });

      await addDoc(collection(db, 'chatRooms', chatRoomId, 'messages'), {
        senderId: fakeUserId,
        receiverId: realUserId,
        message: `${gift.name} hediye etti! 🎁`,
        timestamp: serverTimestamp(),
        type: 'gift',
        giftImageUrl: gift.imageUrl || null,
        giftName: gift.name
      });

      alert(`✅ ${gift.name} başarıyla gönderildi!`);
      setShowGiftModal(false);
      setGiftTargetMsg(null);
    } catch (e) {
      console.error('Gift send error:', e);
      alert('Hediye gönderilemedi: ' + e.message);
    } finally {
      setSendingGift(null);
    }
  };

  const loadUnrepliedMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ YENİ SİSTEM: unansweredMessages koleksiyonunu direkt oku
      // Functions tarafındaki onMessageCreated trigger'ı tarafından doldurulur
      const unansweredQuery = query(
        collection(db, 'unansweredMessages'),
        orderBy('createdAt', 'desc')
      );

      const unansweredSnapshot = await getDocs(unansweredQuery);
      console.log(`Total unanswered messages found: ${unansweredSnapshot.docs.length}`);

      // Aynı chatRoom için sadece en son mesajı tut
      const latestMessagesByRoom = new Map();

      unansweredSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const roomId = data.chatRoomId;
        const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : new Date();

        // Eğer bu chatRoom için daha yeni bir mesaj yoksa ekle
        const existing = latestMessagesByRoom.get(roomId);
        if (!existing || timestamp > existing.date) {
          latestMessagesByRoom.set(roomId, {
            id: doc.id,
            roomId: roomId,
            sender: {
              id: data.senderId,
              name: data.senderName || 'Bilinmeyen',
              avatar: data.senderAvatar || null,
              city: data.senderCity || null,
              age: data.senderAge || null
            },
            receiver: {
              id: data.receiverId,
              name: data.receiverName || 'Doğrulanmış Üye',
              avatar: data.receiverAvatar || null,
              city: data.receiverCity || null,
              age: data.receiverAge || null
            },
            message: data.message || 'Mesaj',
            timestamp: data.timestamp,
            type: data.messageType || 'text',
            date: timestamp,
            isReplied: data.isReplied || false,
            mediaUrl: data.mediaUrl || null,
            durationMillis: data.durationMillis || null
          });
        }
      });

      // Map'i listeye çevir
      const unrepliedList = Array.from(latestMessagesByRoom.values());

      setUnrepliedMessages(unrepliedList);
    } catch (err) {
      console.error('Error loading unreplied messages:', err);
      setError('Mesajlar yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : date.toDate();
    // Türkiye saat dilimine göre göster (Europe/Istanbul)
    return d.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Istanbul'
    });
  };

  const handleIgnoreMessage = async () => {
    if (!selectedMessage) return;

    try {
      // Mesajı görmezden gel - chatRoom'a ignored flag ekle
      const roomRef = doc(db, 'chatRooms', selectedMessage.roomId);
      await updateDoc(roomRef, {
        ignoredAt: new Date(),
        ignoredBy: 'admin' // Admin tarafından görmezden gelindi
      });

      // unansweredMessages koleksiyonundan da sil
      try {
        const unansweredQuery = query(
          collection(db, 'unansweredMessages'),
          where('chatRoomId', '==', selectedMessage.roomId)
        );
        const unansweredSnapshot = await getDocs(unansweredQuery);
        await Promise.all(unansweredSnapshot.docs.map(doc => deleteDoc(doc.ref)));
        console.log(`Deleted ${unansweredSnapshot.docs.length} documents from unansweredMessages (ignore)`);
      } catch (err) {
        console.error('Error deleting from unansweredMessages:', err);
      }

      setShowIgnoreConfirm(false);
      setSelectedMessage(null);
    } catch (err) {
      console.error('Error ignoring message:', err);
      setError('Mesaj görmezden gelinirken hata oluştu.');
    }
  };

  const handleDeleteChat = async () => {
    if (!selectedMessage) return;

    try {
      // Tüm mesajları sil
      const messagesRef = collection(db, 'chatRooms', selectedMessage.roomId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);

      const deletePromises = messagesSnapshot.docs.map(msgDoc =>
        deleteDoc(doc(db, 'chatRooms', selectedMessage.roomId, 'messages', msgDoc.id))
      );
      await Promise.all(deletePromises);

      // ChatRoom'u sil
      await deleteDoc(doc(db, 'chatRooms', selectedMessage.roomId));

      // unansweredMessages koleksiyonundan da sil
      try {
        const unansweredQuery = query(
          collection(db, 'unansweredMessages'),
          where('chatRoomId', '==', selectedMessage.roomId)
        );
        const unansweredSnapshot = await getDocs(unansweredQuery);
        await Promise.all(unansweredSnapshot.docs.map(doc => deleteDoc(doc.ref)));
        console.log(`Deleted ${unansweredSnapshot.docs.length} documents from unansweredMessages`);
      } catch (err) {
        console.error('Error deleting from unansweredMessages:', err);
      }

      setShowDeleteConfirm(false);
      setSelectedMessage(null);
    } catch (err) {
      console.error('Error deleting chat:', err);
      setError('Sohbet silinirken hata oluştu.');
    }
  };

  /*
   * ====================================================================
   * KRİTİK: ONAYLI PROFİL TESPİTİ
   * ====================================================================
   * 
   * Admin panelden HER ZAMAN onaylı profil adına mesaj atılmalıdır.
   * 
   * URL PARAMETRELERİNİN ANLAMI:
   * - receiverId = ONAYLI PROFİL (admin bu profil adına mesaj atar)
   * - senderId = GERÇEK KULLANICI (mesajı alan)
   * 
   * KURALLAR:
   * 1. Her iki kullanıcının verisini çek
   * 2. Onaylı olanı tespit et (isApproved === true veya benzer field)
   * 3. Onaylı profil → receiverId
   * 4. Diğer kullanıcı → senderId
   * 5. Hiçbiri onaylı değilse uyarı ver ve engelle
   * ====================================================================
   */

  const handleReplyMessage = async (message, e) => {
    if (!message) return;

    try {
      // ✅ HER ZAMAN: Admin her zaman alıcı (receiver) adına hareket eder
      // Admin her mesaja girebilir ve cevap verebilir
      // Alıcı her zaman "onaylı profil" rolünde davranır
      const verifiedProfileId = message.receiver.id;
      const realUserId = message.sender.id;

      // ✅ URL oluştur: receiverId = ALICI (admin bu profil adına hareket eder), senderId = GERÇEK KULLANICI
      const url = `/messages/reply/${message.roomId}?receiverId=${verifiedProfileId}&senderId=${realUserId}`;

      console.log('💬 Mesaja gidiliyor:', {
        senderName: message.sender.name,
        receiverName: message.receiver.name,
        verifiedProfileId,
        realUserId,
        url
      });

      // Ctrl+Click veya Cmd+Click ile yeni sekmede aç
      if (e && (e.ctrlKey || e.metaKey)) {
        if (e.preventDefault) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
        window.open(window.location.origin + '/#' + url, '_blank');
        return;
      }

      // Normal tıklama - aynı sayfada aç
      history.push(url);
    } catch (err) {
      console.error('Error navigating to message:', err);
      setError('Mesaj sayfasına gidilirken hata oluştu.');
    }
  };

  // Klavye kısayolları: ArrowUp ve ArrowDown
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Input veya Textarea odaklıyken çalışma
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'ArrowDown') {
        // İlk 10 mesajı aç
        e.preventDefault();
        const batch = filteredMessages.slice(0, 10);
        batch.forEach(msg => {
          const verifiedProfileId = msg.receiver.id;
          const realUserId = msg.sender.id;
          const url = `/messages/reply/${msg.roomId}?receiverId=${verifiedProfileId}&senderId=${realUserId}`;
          window.open(window.location.origin + '/#' + url, '_blank');
        });
      } else if (e.key === 'ArrowUp') {
        // En alttan yukarıya doğru 10 mesajı aç (e.g. 25, 24, ..., 16)
        e.preventDefault();
        const total = filteredMessages.length;
        if (total === 0) return;

        const count = Math.min(10, total);
        const batch = filteredMessages.slice(total - count).reverse();

        batch.forEach(msg => {
          const verifiedProfileId = msg.receiver.id;
          const realUserId = msg.sender.id;
          const url = `/messages/reply/${msg.roomId}?receiverId=${verifiedProfileId}&senderId=${realUserId}`;
          window.open(window.location.origin + '/#' + url, '_blank');
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredMessages]);

  // Tüm cevapsız mesajları sil
  const handleDeleteAllUnrepliedMessages = async () => {
    try {
      setProcessingAll(true);
      setError(null);

      // Tüm cevapsız mesajları al
      const unansweredQuery = query(
        collection(db, 'unansweredMessages'),
        orderBy('createdAt', 'desc')
      );
      const unansweredSnapshot = await getDocs(unansweredQuery);

      if (unansweredSnapshot.empty) {
        alert('Silinecek cevapsız mesaj bulunamadı.');
        setProcessingAll(false);
        setShowDeleteAllConfirm(false);
        return;
      }

      // Tüm chatRoom'ları topla (tekrar edenleri çıkar)
      const uniqueRoomIds = new Set();
      unansweredSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.chatRoomId) {
          uniqueRoomIds.add(data.chatRoomId);
        }
      });

      let deletedCount = 0;
      const batchSize = 500;
      let batch = writeBatch(db);
      let batchCount = 0;

      // Her chatRoom için mesajları ve chatRoom'u sil
      for (const roomId of uniqueRoomIds) {
        try {
          // ChatRoom'daki tüm mesajları sil
          const messagesRef = collection(db, 'chatRooms', roomId, 'messages');
          const messagesSnapshot = await getDocs(messagesRef);

          for (const msgDoc of messagesSnapshot.docs) {
            batch.delete(msgDoc.ref);
            batchCount++;
            deletedCount++;

            if (batchCount >= batchSize) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
          }

          // ChatRoom'u sil
          const roomRef = doc(db, 'chatRooms', roomId);
          batch.delete(roomRef);
          batchCount++;
          deletedCount++;

          if (batchCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        } catch (err) {
          console.error(`Error deleting chatRoom ${roomId}:`, err);
        }
      }

      // unansweredMessages koleksiyonundaki tüm dokümanları sil
      for (const unansweredDoc of unansweredSnapshot.docs) {
        batch.delete(unansweredDoc.ref);
        batchCount++;
        deletedCount++;

        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Kalan batch işlemlerini commit et
      if (batchCount > 0) {
        await batch.commit();
      }

      alert(`Tüm cevapsız mesajlar başarıyla silindi!\n\nToplam ${deletedCount} kayıt temizlendi.`);
      setShowDeleteAllConfirm(false);
    } catch (err) {
      console.error('Error deleting all unreplied messages:', err);
      setError('Tüm cevapsız mesajlar silinirken hata oluştu: ' + err.message);
      alert('Hata: ' + err.message);
    } finally {
      setProcessingAll(false);
    }
  };

  // Tüm cevapsız mesajları görmezden gel
  const handleIgnoreAllUnrepliedMessages = async () => {
    try {
      setProcessingAll(true);
      setError(null);

      // Tüm cevapsız mesajları al
      const unansweredQuery = query(
        collection(db, 'unansweredMessages'),
        orderBy('createdAt', 'desc')
      );
      const unansweredSnapshot = await getDocs(unansweredQuery);

      if (unansweredSnapshot.empty) {
        alert('Görmezden gelinecek cevapsız mesaj bulunamadı.');
        setProcessingAll(false);
        setShowIgnoreAllConfirm(false);
        return;
      }

      // Tüm chatRoom'ları topla (tekrar edenleri çıkar)
      const uniqueRoomIds = new Set();
      unansweredSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.chatRoomId) {
          uniqueRoomIds.add(data.chatRoomId);
        }
      });

      let processedCount = 0;
      const batchSize = 500;
      let batch = writeBatch(db);
      let batchCount = 0;

      // Her chatRoom için ignoredAt flag'i ekle
      for (const roomId of uniqueRoomIds) {
        try {
          const roomRef = doc(db, 'chatRooms', roomId);
          batch.update(roomRef, {
            ignoredAt: new Date(),
            ignoredBy: 'admin'
          });
          batchCount++;
          processedCount++;

          if (batchCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        } catch (err) {
          console.error(`Error updating chatRoom ${roomId}:`, err);
        }
      }

      // unansweredMessages koleksiyonundaki tüm dokümanları sil
      for (const unansweredDoc of unansweredSnapshot.docs) {
        batch.delete(unansweredDoc.ref);
        batchCount++;
        processedCount++;

        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Kalan batch işlemlerini commit et
      if (batchCount > 0) {
        await batch.commit();
      }

      alert(`Tüm cevapsız mesajlar görmezden gelindi!\n\nToplam ${processedCount} kayıt işlendi.`);
      setShowIgnoreAllConfirm(false);
    } catch (err) {
      console.error('Error ignoring all unreplied messages:', err);
      setError('Tüm cevapsız mesajlar görmezden gelinirken hata oluştu: ' + err.message);
      alert('Hata: ' + err.message);
    } finally {
      setProcessingAll(false);
    }
  };

  // Mesajı maksimum 90 karakter ile sınırla (önizleme için)
  const truncateMessage = (message) => {
    if (!message || message.length <= 90) return message;
    return message.substring(0, 90) + '...';
  };

  const filteredMessages = unrepliedMessages.filter(msg => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return msg.sender.name.toLowerCase().includes(search) ||
      msg.receiver.name.toLowerCase().includes(search) ||
      msg.message.toLowerCase().includes(search);
  });

  return (
    <>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center">
          <h4>Cevapsız Mesajlar</h4>
          <div className="d-flex gap-2">
            <Button
              variant="danger"
              onClick={() => setShowDeleteAllConfirm(true)}
              disabled={loading || processingAll || unrepliedMessages.length === 0}
            >
              <FontAwesomeIcon icon={faTrash} />
              <span className="ms-2">Tüm Cevapsız Mesajları Sil</span>
            </Button>
            <Button
              variant="warning"
              onClick={() => setShowIgnoreAllConfirm(true)}
              disabled={loading || processingAll || unrepliedMessages.length === 0}
            >
              <FontAwesomeIcon icon={faEyeSlash} />
              <span className="ms-2">Tümünü Görmezden Gel</span>
            </Button>
            <Button variant="primary" onClick={loadUnrepliedMessages} disabled={loading || processingAll}>
              <FontAwesomeIcon icon={loading ? faSpinner : faComment} className={loading ? "fa-spin" : ""} />
              <span className="ms-2">{loading ? 'Yükleniyor...' : 'Yenile'}</span>
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <div className="mb-4">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Gönderen, alıcı veya mesaj içeriğine göre ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="primary">Ara</Button>
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faSpinner} className="fa-spin fa-2x" />
              <p className="mt-3">Mesajlar yükleniyor...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Cevapsız mesaj bulunamadı.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-dark table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th style={{ width: '200px' }}>Gönderen Üye</th>
                    <th style={{ width: '200px' }}>Alıcı Üye</th>
                    <th>Mesaj</th>
                    <th style={{ width: '180px' }}>Tarih</th>
                    <th style={{ width: '150px' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((msg, index) => (
                    <tr key={msg.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>
                        <div className="d-flex flex-column align-items-center">
                          {msg.sender.avatar ? (
                            <img
                              src={msg.sender.avatar}
                              alt={msg.sender.name}
                              className="rounded-circle mb-1"
                              style={{ width: 50, height: 50, objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary mb-1 d-flex align-items-center justify-content-center text-white"
                              style={{ width: 50, height: 50 }}
                            >
                              {msg.sender.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <small>{msg.sender.name}</small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column align-items-center position-relative">
                          {msg.receiver.avatar ? (
                            <img
                              src={msg.receiver.avatar}
                              alt={msg.receiver.name}
                              className="rounded-circle mb-1"
                              style={{ width: 50, height: 50, objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary mb-1 d-flex align-items-center justify-content-center text-white"
                              style={{ width: 50, height: 50 }}
                            >
                              {msg.receiver.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <small>{msg.receiver.name}</small>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="mt-1 p-1"
                            style={{ fontSize: '0.7rem', lineHeight: 1 }}
                            title={`${msg.receiver.name} adına hediye gönder`}
                            onClick={(e) => openGiftModal(msg, e)}
                          >
                            <FontAwesomeIcon icon={faGift} />
                          </Button>
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          {msg.type === 'image' && <span className="me-2">📷</span>}
                          {msg.type === 'video' && <span className="me-2">🎥</span>}
                          {msg.type === 'audio' && <span className="me-2">🎤</span>}
                          {msg.type === 'gift' && <span className="me-2">🎁</span>}
                          <span>
                            {msg.type === 'gift'
                              ? `${msg.sender.name} size ${msg.message.replace(' hediye etti! 🎁', '').replace(' 🎁', '')} gönderdi`
                              : truncateMessage(msg.message)}
                          </span>
                        </div>
                      </td>
                      <td className="align-middle">{formatDate(msg.date)}</td>
                      <td>
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            variant="primary"
                            size="sm"
                            className="p-2"
                            style={{ width: '35px', height: '35px' }}
                            title="Cevapla"
                            onClick={(e) => handleReplyMessage(msg, e)}
                          >
                            <FontAwesomeIcon icon={faEnvelope} />
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            className="p-2"
                            style={{ width: '35px', height: '35px' }}
                            title="Görmezden Gel"
                            onClick={() => {
                              setSelectedMessage(msg);
                              setShowIgnoreConfirm(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="p-2"
                            style={{ width: '35px', height: '35px' }}
                            title="Sil"
                            onClick={() => {
                              setSelectedMessage(msg);
                              setShowDeleteConfirm(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {!loading && filteredMessages.length > 0 && (
            <div className="mt-3">
              <Badge bg="info">Toplam {filteredMessages.length} cevapsız mesaj</Badge>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Gift Picker Modal */}
      <Modal show={showGiftModal} onHide={() => { setShowGiftModal(false); setGiftTargetMsg(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            🎁 Hediye Gönder — <strong>{giftTargetMsg?.receiver.name}</strong> adına → <strong>{giftTargetMsg?.sender.name}</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {giftsLoading ? (
            <div className="text-center py-4"><FontAwesomeIcon icon={faSpinner} className="fa-spin fa-2x" /></div>
          ) : gifts.length === 0 ? (
            <p className="text-muted text-center">Aktif hediye bulunamadı.</p>
          ) : (
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {gifts.map(gift => (
                <div key={gift.id} className="text-center" style={{ width: 110 }}>
                  {gift.imageUrl && (
                    <img src={gift.imageUrl} alt={gift.name} style={{ width: 70, height: 70, objectFit: 'contain' }} className="mb-1" />
                  )}
                  <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{gift.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{gift.cost} kredi</div>
                  <Button
                    variant="warning"
                    size="sm"
                    className="mt-1 w-100"
                    disabled={!!sendingGift}
                    onClick={() => handleSendGift(gift)}
                  >
                    {sendingGift === gift.id ? <FontAwesomeIcon icon={faSpinner} className="fa-spin" /> : 'Gönder'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowGiftModal(false); setGiftTargetMsg(null); }}>Kapat</Button>
        </Modal.Footer>
      </Modal>

      {/* Görmezden Gel Onay Modal */}
      <Modal show={showIgnoreConfirm} onHide={() => setShowIgnoreConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Mesajı Görmezden Gel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu mesajı görmezden gelmek istediğinizden emin misiniz?
          Aynı sohbetten yeni mesaj gelirse tekrar cevapsız mesajlara düşecektir.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIgnoreConfirm(false)}>
            Hayır
          </Button>
          <Button variant="warning" onClick={handleIgnoreMessage}>
            Evet
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sohbet Silme Onay Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Sohbeti Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu sohbeti tamamen silmek istediğinizden emin misiniz?
          Bu işlem geri alınamaz ve uygulama tarafında da sohbet silinecektir.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Hayır
          </Button>
          <Button variant="danger" onClick={handleDeleteChat}>
            Evet, Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tüm Cevapsız Mesajları Sil Onay Modal */}
      <Modal show={showDeleteAllConfirm} onHide={() => !processingAll && setShowDeleteAllConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Tüm Cevapsız Mesajları Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>UYARI:</strong> Bu işlem geri alınamaz!
          </Alert>
          <p>
            Tüm cevapsız mesajları silmek istediğinize emin misiniz?
          </p>
          <ul>
            <li>Tüm sohbetler ve mesajlar <strong>tamamen silinecek</strong></li>
            <li>Uygulama tarafında da sohbetler görünmeyecek</li>
            <li>Bu işlem <strong>geri alınamaz</strong></li>
          </ul>
          <p className="mb-0">
            <strong>Toplam {unrepliedMessages.length} cevapsız mesaj silinecek.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteAllConfirm(false)}
            disabled={processingAll}
          >
            İptal
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllUnrepliedMessages}
            disabled={processingAll}
          >
            {processingAll ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Siliniyor...
              </>
            ) : (
              'Evet, Tümünü Sil'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tümünü Görmezden Gel Onay Modal */}
      <Modal show={showIgnoreAllConfirm} onHide={() => !processingAll && setShowIgnoreAllConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Tümünü Görmezden Gel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tüm cevapsız mesajları görmezden gelmek istediğinize emin misiniz?
          </p>
          <ul>
            <li>Mesajlar <strong>silinmeyecek</strong>, sadece cevapsız mesajlardan çıkarılacak</li>
            <li>ChatRoom'lar "Okundu" olarak işaretlenecek</li>
            <li>Aynı sohbetten yeni mesaj gelirse tekrar cevapsız mesajlara düşecektir</li>
          </ul>
          <p className="mb-0">
            <strong>Toplam {unrepliedMessages.length} cevapsız mesaj görmezden gelinecek.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowIgnoreAllConfirm(false)}
            disabled={processingAll}
          >
            İptal
          </Button>
          <Button
            variant="warning"
            onClick={handleIgnoreAllUnrepliedMessages}
            disabled={processingAll}
          >
            {processingAll ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                İşleniyor...
              </>
            ) : (
              'Evet, Tümünü Görmezden Gel'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
